import React, { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import './ToolPage.css'
import './PlanningPoker.css'
import ConfirmModal from './ConfirmModal'
import PokerSeat from './PokerSeat'
import {
  fetchPlanningPokerState,
  planningPokerStreamUrl,
  planningPokerJoinUrl,
  revealPlanningPokerVotes,
  resetPlanningPokerRound,
  removePlanningPokerParticipant,
} from './utils/planningPokerApiClient'
import { averageVotes } from './utils/planningPokerAverage'
import { trackEvent } from '../../lib/analytics'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

const PlanningPokerMaster = () => {
  useDocumentMeta({
    title: 'Planning Poker, Mestre da Sala | jarder.dev',
    description: 'Acompanhe quem já votou e revele as cartas quando todos estiverem prontos.',
  })

  const { id } = useParams()
  const masterToken = (/token=([^&]+)/.exec(window.location.hash) || [])[1] || ''

  const [state, setState] = useState(null)
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState('')
  const [copied, setCopied] = useState(false)
  const [topicInput, setTopicInput] = useState('')
  const [pendingConfirm, setPendingConfirm] = useState(null)

  const joinUrl = planningPokerJoinUrl(id)

  useEffect(() => {
    let cancelled = false

    fetchPlanningPokerState(id)
      .then((data) => {
        if (cancelled) return
        setState(data)
        setTopicInput(data.topic)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('notfound')
      })

    const eventSource = new EventSource(planningPokerStreamUrl(id))
    eventSource.onmessage = (event) => {
      setState(JSON.parse(event.data))
    }

    return () => {
      cancelled = true
      eventSource.close()
    }
  }, [id])

  const reveal = async () => {
    setError('')
    try {
      await revealPlanningPokerVotes(id, masterToken)
      trackEvent('tool_generate', { tool: 'planning_poker_reveal' })
    } catch (err) {
      setError(err.message || 'Não foi possível revelar. Confira se você está na tela de mestre certa.')
    }
  }

  const startNewRound = async () => {
    setError('')
    try {
      await resetPlanningPokerRound(id, masterToken, topicInput)
      trackEvent('tool_generate', { tool: 'planning_poker_new_round' })
    } catch (err) {
      setError(err.message || 'Não foi possível iniciar a nova rodada.')
    }
  }

  const removeParticipant = async (participantId) => {
    setError('')
    try {
      await removePlanningPokerParticipant(id, masterToken, participantId)
      trackEvent('tool_generate', { tool: 'planning_poker_remove_participant' })
    } catch (err) {
      setError(err.message || 'Não foi possível excluir o participante.')
    }
  }

  const confirmRemoveParticipant = (participantId, name) => {
    setPendingConfirm({
      message: `Excluir ${name} da sala?`,
      confirmLabel: 'Excluir',
      onConfirm: () => removeParticipant(participantId),
    })
  }

  const copyJoinLink = () => {
    navigator.clipboard.writeText(joinUrl)
    setCopied(true)
    trackEvent('tool_copy', { tool: 'planning_poker' })
  }

  if (status === 'loading') {
    return (
      <div className="tool-page">
        <p>Carregando...</p>
      </div>
    )
  }

  if (status === 'notfound') {
    return (
      <div className="tool-page">
        <Link to="/ferramentas" className="tool-back-link">
          ← Todas as ferramentas
        </Link>
        <h1>Sala não encontrada</h1>
        <p className="tool-disclaimer">Essa sala não existe ou expirou.</p>
        <Link to="/ferramentas/planning-poker" className="tool-button">
          Criar uma nova sala
        </Link>
      </div>
    )
  }

  const votedCount = state.participants.filter((p) => p.voted).length
  const avg = state.revealed ? averageVotes(state.participants) : null

  return (
    <div className="tool-page">
      <Link to="/ferramentas" className="tool-back-link">
        ← Todas as ferramentas
      </Link>
      <h1>Planning Poker, Mestre da Sala</h1>
      <p className="tool-disclaimer">
        Essa tela é só sua. Compartilhe o link abaixo com os
        participantes. Os votos ficam ocultos até você clicar em
        revelar, nem você vê antes disso.
      </p>

      <div className="tool-result-card">
        <span className="tool-result-label">Link pra participantes</span>
        <span className="tool-result-value" style={{ wordBreak: 'break-all' }}>
          {joinUrl}
        </span>
        <div className="tool-result-actions">
          <button type="button" className="tool-button tool-button-secondary" onClick={copyJoinLink}>
            {copied ? 'Copiado!' : 'Copiar link'}
          </button>
        </div>
      </div>

      <div className="poker-topic-row">
        <input
          type="text"
          value={topicInput}
          onChange={(e) => setTopicInput(e.target.value)}
          placeholder="Tópico sendo votado (opcional)"
        />
      </div>

      {state.topic && <p className="poker-topic-display">{state.topic}</p>}

      {error && <p className="tool-error">{error}</p>}

      <div className="poker-actions-row">
        <button type="button" className="tool-button" onClick={reveal} disabled={state.revealed}>
          {state.revealed ? 'Votos revelados' : `Revelar votos (${votedCount}/${state.participants.length})`}
        </button>
        <button type="button" className="tool-button tool-button-secondary" onClick={startNewRound}>
          Nova rodada
        </button>
      </div>

      {avg !== null && <p className="poker-average">Média: {avg}</p>}

      <h2 className="poker-participants-heading">
        Participantes ({state.participants.length})
      </h2>

      {state.participants.length === 0 ? (
        <p className="tool-import-hint">
          Ninguém entrou ainda. Compartilhe o link acima.
        </p>
      ) : (
        <div className="poker-table">
          {state.participants.map((participant) => (
            <PokerSeat
              key={participant.id}
              participant={participant}
              revealed={state.revealed}
              onRemove={confirmRemoveParticipant}
            />
          ))}
        </div>
      )}

      {pendingConfirm && (
        <ConfirmModal
          message={pendingConfirm.message}
          confirmLabel={pendingConfirm.confirmLabel}
          onConfirm={() => {
            pendingConfirm.onConfirm()
            setPendingConfirm(null)
          }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  )
}

export default PlanningPokerMaster
